import { assignedInquiryPageSchema, clientLogShipResultSchema, depositResultSchema, mediaPolicySchema, mediaUploadTicketSchema, tourInternalMeetingsSchema, vocabularyEntrySchema, } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
import { queryString } from './query-string.js';
const vocabularyListSchema = z.array(vocabularyEntrySchema);
/**
 * Les appels de la synchro terrain (D4) : ce qui monte de la tablette, et ce qui y descend.
 *
 * Tous portent le jeton d'accès **en argument** plutôt que de le lire quelque part : côté
 * tablette il vient d'une session qui peut être dégradée, et le rendre explicite oblige
 * l'appelant à décider quoi faire quand il n'y en a pas — au lieu de partir sans identité et
 * de collectionner des 401.
 */
export function createSyncEndpoints(http) {
    return {
        /**
         * Dépose un contrôle. **Le rejeu d'un envoi interrompu est normal et attendu** : c'est
         * `submission.idempotencyKey` qui empêche le doublon côté serveur, pas la prudence de
         * l'appelant. `replayed` dit lequel des deux cas s'est produit.
         */
        submitInspection(accessToken, submission) {
            return http.postJson('/sync/inspections', submission, depositResultSchema, bearer(accessToken));
        },
        /**
         * Dépose un média sous l'identifiant que la tablette lui a donné (M7) — le même à chaque
         * tentative, ce qui rend le rejeu inoffensif sans registre à tenir.
         */
        uploadMedia(accessToken, id, blob) {
            return http.putBinary(`/sync/media/${id}`, blob, bearer(accessToken));
        },
        /**
         * Relit les octets d'un média **de l'équipe** du jeton (#675) — c'est par là que descendent
         * les pièces qu'une contre-visite reprend, déjà dupliquées sous la clé de l'équipe.
         */
        downloadMedium(accessToken, id) {
            return http.getBinary(`/sync/media/${id}`, bearer(accessToken));
        },
        /**
         * Une URL d'envoi direct au stockage pour une vidéo (#418), ou `relay` quand le serveur n'en
         * signe pas. À redemander à chaque tentative : elle ne vaut que quinze minutes.
         */
        mediaUploadUrl(accessToken, id, request) {
            return http.postJson(`/sync/media/${id}/upload-url`, request, mediaUploadTicketSchema, bearer(accessToken));
        },
        /** La limite d'une vidéo que le serveur accepte : 1 Go en direct, 25 Mo par le relais. */
        mediaPolicy(accessToken) {
            return http.get('/sync/media-policy', mediaPolicySchema, bearer(accessToken));
        },
        /**
         * La tournée de l'équipe portée par le jeton — le tri « mes contrôles » est relatif à elle.
         *
         * ⚠️ **La réponse est une page, pas la tournée.** Le serveur en rend 50 par défaut et plafonne
         * ce qu'on lui demande : c'est `total` qui dit combien il y en a, et l'appelant qui rappelle
         * cette fonction avec l'`offset` suivant tant qu'il ne les a pas tous. Ignorer `total`, c'est
         * partir avec une tournée tronquée sans que rien ne le signale.
         */
        listInquiries(accessToken, page = {}) {
            return http.get(`/inquiries${queryString(page)}`, assignedInquiryPageSchema, bearer(accessToken));
        },
        /**
         * Les points internes de l'équipe portée par le jeton, pour son agenda (#319).
         *
         * ⚠️ **La fenêtre est celle du serveur** (`from` inclus, `to` exclu) : c'est sur ces jours-là,
         * et eux seuls, que la tablette remplace ce qu'elle garde. Aucun paramètre ne la demande —
         * la calculer ici la ferait dépendre de l'horloge de la tablette.
         */
        listInternalMeetings(accessToken) {
            return http.get('/planning/tour/internal-meetings', tourInternalMeetingsSchema, bearer(accessToken));
        },
        /** Le référentiel d'anomalies (M17), descendu en entier : il tient, et il sert hors réseau. */
        listVocabulary(accessToken) {
            return http.get('/vocabulary', vocabularyListSchema, bearer(accessToken));
        },
        /**
         * Expédie un lot du journal local (D15). Le rejeu est inoffensif — le serveur ignore les
         * lignes déjà reçues (`(deviceId, sessionId, seq)`) et `accepted: 0` est un succès.
         */
        shipLogs(accessToken, batch) {
            return http.postJson('/sync/logs', batch, clientLogShipResultSchema, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=sync.js.map